import React, { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { Database, AlertTriangle, FileCode2, Boxes, Layers, ListTree, ShieldCheck, Shapes, Workflow, Image as ImageIcon, Palette, Sprout } from 'lucide-react';
import { PageLayout } from '../../shared/PageLayout';
import { AdminTabs } from '../shared/AdminTabs';
import { ConnectionBadge } from '../shared/ConnectionBadge';
import { Card } from '../../../components/ui/Card';
import { getSsotRegistry } from '../../../governance';
import type { SsotEntry, SsotDomain, SsotSnapshot } from '../../../governance';
import { getPlatformSsotEntries } from '../system/ssot/platformSsotEntries';
// ─── Domain presentation config ─────────────────────────────────────────────
// `design` staat hier niet meer: design-tokens hebben hun eigen pagina, en dit
// register toonde er een onvollediger tabelversie van. Zie ssotRegistry/types.ts.
//
// `platform` staat vooraan: dat zijn de bronnen die een beheerder in zijn eigen
// schermen terugziet (rolnamen, groepen, pseudoniemen), dus daar begint hij.
const DOMAIN_ORDER: SsotDomain[] = ['platform', 'taxonomy', 'cognitive', 'governance', 'pipeline', 'content', 'visuals', 'mastery'];
const DOMAIN_CONFIG: Record<SsotDomain, {
  label: string;
  icon: React.ReactNode;
  badge: string;
  dot: string;
  description: string;
}> = {
  taxonomy: {
    label: 'Taxonomie',
    icon: <ListTree className="w-4 h-4" data-id="element-7558" data-mp="129zhldvwn7hb" />,
    badge: 'bg-indigo-50 text-indigo-700 border-indigo-200',
    dot: 'bg-indigo-500',
    description: 'Tiers en de canonieke leerstof-strook.'
  },
  cognitive: {
    label: 'Cognitief',
    icon: <Layers className="w-4 h-4" data-id="element-7559" data-mp="ta30pyfrbcwt" />,
    badge: 'bg-violet-50 text-violet-700 border-violet-200',
    dot: 'bg-violet-500',
    description: 'DOK-floors/ceilings, cognitieve profielen en bounds.'
  },
  governance: {
    label: 'Governance',
    icon: <ShieldCheck className="w-4 h-4" data-id="element-7560" data-mp="2nl7ywn82qx4r" />,
    badge: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    dot: 'bg-emerald-500',
    description: 'Vraagtype-metadata die validatie en gating stuurt.'
  },
  pipeline: {
    label: 'Pipeline',
    icon: <Workflow className="w-4 h-4" data-id="element-7561" data-mp="3vlgf12bvveg2" />,
    badge: 'bg-rose-50 text-rose-700 border-rose-200',
    dot: 'bg-rose-500',
    description: 'Stages, issue-codes, severity-ladder en scoring-gewichten.'
  },
  visuals: {
    label: 'Visuals',
    icon: <ImageIcon className="w-4 h-4" data-id="element-7562" data-mp="3mnwclq2abc3v" />,
    badge: 'bg-amber-50 text-amber-700 border-amber-200',
    dot: 'bg-amber-500',
    description: 'Visual-rollen per vraagtype.'
  },
  content: {
    label: 'Content',
    icon: <Shapes className="w-4 h-4" data-id="element-7563" data-mp="j7gwttvkmdky" />,
    badge: 'bg-sky-50 text-sky-700 border-sky-200',
    dot: 'bg-sky-500',
    description: 'Content-structuur SSOTs.'
  },
  mastery: {
    label: 'Mastery',
    icon: <Sprout className="w-4 h-4" data-id="element-7564" data-mp="9dgrbqwdn6bp" />,
    badge: 'bg-teal-50 text-teal-700 border-teal-200',
    dot: 'bg-teal-500',
    description: 'Adaptieve-engine parameters, groeistadia en difficulty-proxycontract.'
  },
  platform: {
    label: 'Platform',
    icon: <ShieldCheck className="w-4 h-4" data-id="element-7565" data-mp="19gewh2wzh4j1" />,
    badge: 'bg-slate-100 text-slate-700 border-slate-300',
    dot: 'bg-slate-500',
    description: 'Rolnamen, pseudoniemen, groepen, beheersingsbanden en het break-glass-venster — de bronnen die je in deze schermen zelf terugziet.'
  }
};
/**
 * SsotRegistryPage — dedicated, top-level admin surface for every catalogued
 * single-source-of-truth. Each domain gets its own tab; within a tab every
 * SSOT is rendered with its metadata and a LIVE values table (read at render
 * time via `entry.read()`). Drift notes surface as amber cautions. The whole
 * surface is read-only and fully data-driven — new registry entries appear
 * automatically with zero changes here.
 */
export function SsotRegistryPage() {
  // Twee herkomsten in de code (governance-laag mag niets van portalen weten),
  // één register voor de beheerder. Zie platformSsotEntries.ts.
  const entries = useMemo(() => [...getSsotRegistry(), ...getPlatformSsotEntries()], []);
  const grouped = useMemo(() => {
    const map = new Map<SsotDomain, SsotEntry[]>();
    entries.forEach(entry => {
      const list = map.get(entry.domain) ?? [];
      list.push(entry);
      map.set(entry.domain, list);
    });
    return map;
  }, [entries]);
  // Domains that actually carry entries, in canonical order, with any unknown
  // domains appended so nothing is ever silently hidden.
  const domains = useMemo(() => {
    const present = Array.from(grouped.keys());
    const ordered = DOMAIN_ORDER.filter(d => present.includes(d));
    const extras = present.filter(d => !DOMAIN_ORDER.includes(d));
    return [...ordered, ...extras];
  }, [grouped]);
  const notesCount = useMemo(() => entries.filter(e => e.notes).length, [entries]);
  const [activeTab, setActiveTab] = useState<SsotDomain | ''>(() => domains[0] ?? '');
  const tabs = useMemo(() => domains.map(domain => {
    const cfg = DOMAIN_CONFIG[domain];
    return {
      id: domain,
      label: cfg?.label ?? domain,
      icon: cfg?.icon ?? <Boxes className="w-4 h-4" data-id="element-7566" data-mp="lmwg1lgsjx00" />
    };
  }), [domains]);
  const activeDomain = (activeTab || domains[0]) as SsotDomain | undefined;
  const activeEntries = activeDomain ? grouped.get(activeDomain) ?? [] : [];
  const activeCfg = activeDomain ? DOMAIN_CONFIG[activeDomain] : undefined;
  return <PageLayout title="SSOT Registry" subtitle="Alle single-sources-of-truth — data-driven en live uit de code gelezen" actions={<ConnectionBadge status="live" data-id="element-7568" data-mp="15d8gjvncuit1" />} breadcrumbs={[{
    label: 'Inhoud'
  }, {
    label: 'SSOT Registry'
  }]} data-id="element-7567" data-mp="hnnmohpny14z">
      
      {/* ── Summary header ── */}
      <Card className="p-5 mb-5" data-id="element-7569" data-mp="vnf5cvr4k5pb">
        <div className="flex items-start gap-4" data-id="element-7570" data-mp="vmdlq2w1mavz">
          <div className="w-10 h-10 rounded-xl bg-gray-900 text-white flex items-center justify-center flex-shrink-0" data-id="element-7571" data-mp="10l6x9prrrrti">
            <Database className="w-5 h-5" data-id="element-7572" data-mp="1oa9o9o8amx0o" />
          </div>
          <div className="min-w-0" data-id="element-7573" data-mp="1t217tet182hb">
            <h2 className="text-lg font-bold text-gray-900 leading-tight" data-id="element-7574" data-mp="1t32tsebl69dt">
              Single-Sources-of-Truth
            </h2>
            <p className="text-sm text-gray-600 mt-1 leading-relaxed" data-id="element-7575" data-mp="22x8ejfvezldt">
              <span className="font-semibold text-gray-900 tabular-nums" data-id="element-7576" data-mp="1xzyel6aefrbq">
                {entries.length}
              </span>{' '}
              gecatalogiseerde single-sources-of-truth, verdeeld over{' '}
              <span className="font-semibold text-gray-900 tabular-nums" data-id="element-7577" data-mp="1nzxuvj8mb970">
                {domains.length}
              </span>{' '}
              {domains.length === 1 ? 'domein' : 'domeinen'}. Elke tabel toont
              de actuele waarden zoals de draaiende app ze nu hanteert.
              {notesCount > 0 && <>
                  {' '}
                  <span className="text-amber-700 font-medium" data-id="element-7578" data-mp="15a9hh40t8bfe">
                    {notesCount} {notesCount === 1 ? 'entry' : 'entries'}
                  </span>{' '}
                  met een bekende drift-notitie.
                </>}
            </p>
          </div>
        </div>
      </Card>

      {/* Verwijzing in plaats van een tweede tokentabel. Dit register had een
          `design`-tab die dezelfde tokens toonde als de Design System-pagina,
          maar minder volledig. Eén surface wint; de weg erheen blijft. */}
      <Card className="p-4 mb-5 border-fuchsia-200 bg-fuchsia-50/50" data-id="element-7579" data-mp="3gxbjia555m7y">
        <div className="flex items-start gap-3" data-id="element-7580" data-mp="3lwo4epcgcj7w">
          <span className="mt-0.5 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-fuchsia-100 text-fuchsia-700" data-id="element-7581" data-mp="3lvd3qyeazrl0">
            <Palette className="h-4 w-4" aria-hidden="true" data-id="element-7582" data-mp="2o72y89fb6lp5" />
          </span>
          <p className="text-sm leading-relaxed text-fuchsia-900" data-id="element-7583" data-mp="2t8k0o9dfyg7u">
            <span className="font-bold" data-id="element-7584" data-mp="2y6tx0nawjmv1">Design-tokens staan niet hier.</span>{' '}
            Kleur, vorm, module-palet, rol- en mastery-kleuren hebben hun eigen
            pagina onder <span className="font-semibold" data-id="element-7585" data-mp="25ipkpixtsbjd">Design System</span> —
            met swatches in plaats van hex-tabellen, want een kleur is niet uit
            een tekstwaarde te beoordelen. Bron:{' '}
            <code className="rounded bg-fuchsia-100 px-1.5 py-0.5 font-mono text-[12px]" data-id="element-7586" data-mp="13tx3fibxoom1">
              src/config/designTokens.ts
            </code>
          </p>
        </div>
      </Card>

      {/* ── Domain tabs ── */}
      {tabs.length > 0 && <AdminTabs tabs={tabs} activeTab={activeDomain ?? ''} onTabChange={id => setActiveTab(id as SsotDomain)} data-id="element-7587" data-mp="1cvo8qm2katfd" />}

      {activeCfg?.description && <p className="text-sm text-gray-500 mb-5" data-id="element-7588" data-mp="1mt2pkmsedk87">{activeCfg.description}</p>}

      {/* ── Active domain entries ── */}
      <div role="tabpanel" id={`tabpanel-${activeDomain ?? 'none'}`} className="space-y-4" data-id="element-7589" data-mp="1huho6k1esui6">
        
        {activeEntries.length === 0 ? <p className="text-sm text-gray-400 italic" data-id="element-7590" data-mp="1vk305agsk9gp">
            Geen SSOT-bronnen in dit domein.
          </p> : activeEntries.map((entry, idx) => <SsotEntryCard key={entry.id} entry={entry} dot={activeCfg?.dot ?? 'bg-gray-400'} badge={activeCfg?.badge ?? 'bg-gray-50 text-gray-700 border-gray-200'} domainLabel={activeCfg?.label ?? activeDomain ?? ''} index={idx} data-id="element-7591" data-mp="1rm5ay7n0tndb" data-mpi={idx} />)}
      </div>
    </PageLayout>;
}
// ─── Entry card ───────────────────────────────────────────────────────────
function SsotEntryCard({
  entry,
  dot,
  badge,
  domainLabel,
  index
}: {
  entry: SsotEntry;
  dot: string;
  badge: string;
  domainLabel: string;
  index: number;
}) {
  // LIVE read at render time — never cached, never copied.
  const snapshot = useMemo(() => entry.read(), [entry]);
  // Draagt deze bron een live bewaking? Dan hoort de uitkomst hier te staan en
  // niet alleen in Diagnostiek: bij de bron zelf is het antwoord op "mag ik hier
  // op vertrouwen" het meest waard.
  const guard = useMemo(() => entry.guard?.() ?? null, [entry]);
  return <motion.div initial={{
    opacity: 0,
    y: 6
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    duration: 0.25,
    delay: Math.min(index * 0.04, 0.2)
  }} data-id="element-7592" data-mp="1rfr1gqhqyv4l">
      
      <Card className="p-5" data-id="element-7593" data-mp="1ij07qkqo3sd6">
        {/* Header */}
        <div className="flex items-start justify-between gap-3 mb-3" data-id="element-7594" data-mp="1miqv0egzjqcy">
          <div className="min-w-0" data-id="element-7595" data-mp="1md6vzc3j3sl8">
            <div className="flex items-center gap-2 flex-wrap" data-id="element-7596" data-mp="1mc5hsyr7upza">
              <span className={`w-2 h-2 rounded-full flex-shrink-0 ${dot}`} data-id="element-7597" data-mp="1rc0a96qn3nmg" />
              <h4 className="text-sm font-semibold text-gray-900" data-id="element-7598" data-mp="1ra74dtnup7fi">
                {entry.name}
              </h4>
              <span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded border ${badge}`} data-id="element-7599" data-mp="1he428nt018sk">
                
                {domainLabel}
              </span>
            </div>
            <p className="text-xs text-gray-600 mt-1.5 leading-relaxed" data-id="element-7600" data-mp="2a2i0n4o0274n">
              {entry.description}
            </p>
          </div>
        </div>

        {/* Live bewaking, alleen als de vergelijking in de browser te maken is. */}
        {guard && <div role="status" className={`mb-3 flex items-start gap-2.5 rounded-lg border px-3 py-2.5 ${guard.ok ? 'border-emerald-200 bg-emerald-50' : 'border-red-200 bg-red-50'}`} data-id="element-7601" data-mp="20bnahp7mundx">
            {guard.ok ? <ShieldCheck className="mt-0.5 h-4 w-4 flex-shrink-0 text-emerald-600" aria-hidden="true" data-id="element-7602" data-mp="2su2et1ndycyj" /> : <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0 text-red-600" aria-hidden="true" data-id="element-7603" data-mp="hcz7n264meyi" />}
            <p className={`text-[11px] leading-relaxed ${guard.ok ? 'text-emerald-900' : 'text-red-900'}`} data-id="element-7604" data-mp="ceflp1rj9365">
              <span className="font-bold" data-id="element-7605" data-mp="r3k4ol65jbfb">
                {guard.ok ? 'Nu gecontroleerd — staat gelijk.' : 'Nu gecontroleerd — gedrift.'}
              </span>{' '}
              {guard.detail}
            </p>
          </div>}

        {/* Metadata row */}
        <div className="flex flex-col gap-2 mb-3 text-[11px]" data-id="element-7606" data-mp="3plz6p5y4e86p">
          <div className="flex items-center gap-2 text-gray-500" data-id="element-7607" data-mp="3pntr8j5hbya9">
            <FileCode2 className="w-3.5 h-3.5 flex-shrink-0 text-gray-400" data-id="element-7608" data-mp="8edgwjqi3tmn" />
            <code className="font-mono text-gray-700 break-all" data-id="element-7609" data-mp="3dzg9mr8blyn">
              {entry.file}
            </code>
            <span className="text-gray-300" data-id="element-7610" data-mp="1jrpo94lgyudk">·</span>
            <code className="font-mono text-gray-700 break-all" data-id="element-7611" data-mp="1juloizkshv56">
              {entry.exportName}
            </code>
          </div>
          <p className="text-gray-500 leading-relaxed" data-id="element-7612" data-mp="1lxdwy0qsercv">
            <span className="font-medium text-gray-700" data-id="element-7613" data-mp="1lyypjktjk083">Bepaalt:</span>{' '}
            {entry.governs}
          </p>
          {entry.consumers.length > 0 && <div className="flex items-start gap-2 flex-wrap" data-id="element-7614" data-mp="1vusds1gmhi5p">
              <span className="font-medium text-gray-700 pt-0.5" data-id="element-7615" data-mp="1vt7drwbjh70g">
                Consumers:
              </span>
              <div className="flex flex-wrap gap-1" data-id="element-7616" data-mp="136ms9chqvp4t">
                {entry.consumers.map(consumer => <span key={consumer} className="inline-flex items-center px-1.5 py-0.5 rounded bg-gray-100 text-gray-600 font-mono text-[10px]" data-id="element-7617" data-mp="185h7y5kf0xkp">
                
                    {consumer}
                  </span>)}
              </div>
            </div>}
        </div>

        {/* Drift / caution note */}
        {entry.notes && <div role="note" className="flex items-start gap-2.5 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 mb-3" data-id="element-7618" data-mp="kgeu78swjovh">
          
            <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" data-id="element-7619" data-mp="pdez3ge0mll0" />
            <p className="text-[11px] text-amber-900 leading-relaxed" data-id="element-7620" data-mp="32ny9ygq2zjtd">
              {entry.notes}
            </p>
          </div>}

        {/* Live snapshot table */}
        <SnapshotTable snapshot={snapshot} data-id="element-7621" data-mp="2to1qqumgnd5p" />
      </Card>
    </motion.div>;
}
// ─── Snapshot table ─────────────────────────────────────────────────────────
function SnapshotTable({
  snapshot
}: {
  snapshot: SsotSnapshot;
}) {
  if (snapshot.rows.length === 0) {
    return <p className="text-[11px] text-gray-400 italic px-1 py-2" data-id="element-7622" data-mp="2js860nyl8dcu">
        Geen waarden om te tonen.
      </p>;
  }
  return <div className="overflow-x-auto -mx-1 px-1 rounded-lg border border-gray-100" data-id="element-7623" data-mp="2axtsaptphavu">
      <table className="min-w-full text-[11px]" data-id="element-7624" data-mp="2avpd3bqcwpg9">
        <thead data-id="element-7625" data-mp="2601oaxfsdyba">
          <tr className="bg-gray-50" data-id="element-7626" data-mp="260jvuq7wfyyi">
            {snapshot.columns.map(col => <th key={col.key} className="text-left font-semibold text-gray-600 uppercase tracking-wider px-3 py-2 whitespace-nowrap border-b border-gray-100" data-id="element-7627" data-mp="2105jwx36g4v7">
              
                {col.label}
              </th>)}
          </tr>
        </thead>
        <tbody data-id="element-7628" data-mp="3w5577cu3y9u0">
          {snapshot.rows.map((row, rowIdx) => <tr key={rowIdx} className={rowIdx % 2 === 1 ? 'bg-gray-50/50' : 'bg-white'} data-id="element-7629" data-mp="4vpqnn66mqwp" data-mpi={rowIdx}>
            
              {snapshot.columns.map(col => {
            const value = row[col.key];
            return <td key={col.key} className="px-3 py-1.5 text-gray-700 tabular-nums whitespace-nowrap border-b border-gray-50 font-mono" data-id="element-7630" data-mp="3rbk6ve51l3rn">
                  
                    {value === undefined || value === '' ? <span className="text-gray-300" data-id="element-7631" data-mp="3ibxgdndupidd">—</span> : value}
                  </td>;
          })}
            </tr>)}
        </tbody>
      </table>
    </div>;
}